// 약관 개정 안내 폼 상태 ↔ 발송 API 요청 변환
const EN_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const pad = (n) => String(n).padStart(2, '0');

const parseIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
};

export const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const formatNoticeDate = (iso) => {
  const { y, m, d } = parseIso(iso);
  return `${y}.${pad(m)}.${pad(d)}`;
};

export const formatDateKo = (iso) => {
  const { y, m, d } = parseIso(iso);
  return `${y}년 ${m}월 ${d}일`;
};

export const formatDateEn = (iso) => {
  const { y, m, d } = parseIso(iso);
  return `${EN_MONTHS[m - 1]} ${d}, ${y}`;
};

export const daysBetween = (fromIso, toIso) =>
  Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86400000);

export const SENDER_DOMAINS = ['ko-ready.com', 'oinglab-korean.com'];

export const TWO_DAYS_AUDIENCE = 'TWO_DAYS_PARTICIPANTS';
export const TWO_DAYS_BRAND = '2days project';

export const AUDIENCES = [
  {
    value: 'ALL_MEMBERS',
    en: 'All Koready members',
    ko: '코레디 전체 회원',
    descEn: 'Every active member with an email address',
    descKo: '이메일이 있는 활성 회원 전체',
  },
  {
    value: TWO_DAYS_AUDIENCE,
    en: '2days participants',
    ko: '2days 참여자',
    descEn: 'Members who agreed to the 2days Terms at Pre-join',
    descKo: 'Pre-join에서 2days 약관에 동의한 회원만',
  },
];

const SENDER_LOCAL = /^[A-Za-z0-9._%+-]+$/;

export const emptyRow = () => ({ ko: '', en: '' });

export const initialForm = () => ({
  audience: AUDIENCES[0].value,
  terms: true,
  privacy: true,
  noticeDate: todayIso(),
  effectiveDate: '',
  archiveDate: '',
  subject: '',
  reasons: [emptyRow()],
  termsChanges: [emptyRow()],
  privacyChanges: [emptyRow()],
  noticeUrl: '',
  campaignKey: '',
  brandName: '',
  senderName: '',
  senderLocal: '',
  senderDomain: SENDER_DOMAINS[0],
});

export const deriveCampaignKey = (form) => {
  if (!form.effectiveDate) return '';
  const parts = [
    form.terms && 'terms',
    form.privacy && 'privacy',
    form.audience === TWO_DAYS_AUDIENCE && '2days',
  ];
  return `policy-update-${form.effectiveDate}-${parts.filter(Boolean).join('-')}`;
};

export const campaignKeyOf = (form) =>
  form.campaignKey.trim() || deriveCampaignKey(form);

const filled = (rows) =>
  rows
    .map((row) => ({ ko: row.ko.trim(), en: row.en.trim() }))
    .filter((row) => row.ko || row.en);

export const toPayload = (form) => ({
  audience: form.audience,
  campaignKey: campaignKeyOf(form),
  subject: form.subject.trim() || null,
  noticeDate: form.noticeDate ? formatNoticeDate(form.noticeDate) : '',
  effectiveDateKo: form.effectiveDate ? formatDateKo(form.effectiveDate) : '',
  effectiveDateEn: form.effectiveDate ? formatDateEn(form.effectiveDate) : '',
  archiveDateKo: form.archiveDate ? formatDateKo(form.archiveDate) : null,
  archiveDateEn: form.archiveDate ? formatDateEn(form.archiveDate) : null,
  reasons: filled(form.reasons),
  termsChanges: form.terms ? filled(form.termsChanges) : [],
  privacyChanges: form.privacy ? filled(form.privacyChanges) : [],
  noticeUrl: form.noticeUrl.trim() || null,
  senderEmail: form.senderLocal.trim()
    ? `${form.senderLocal.trim()}@${form.senderDomain}`
    : null,
  senderName: form.senderName.trim() || null,
  brandName: form.brandName.trim() || null,
});

export const validateForm = (form) => {
  const errors = [];

  if (!form.terms && !form.privacy) {
    errors.push([
      'Pick at least one document that changed.',
      '개정 대상 문서를 하나 이상 선택해 주세요.',
    ]);
  }
  if (!form.noticeDate || !form.effectiveDate) {
    errors.push([
      'Enter both the notice date and the effective date.',
      '공지일과 시행일을 모두 입력해 주세요.',
    ]);
  } else if (daysBetween(form.noticeDate, form.effectiveDate) < 0) {
    errors.push([
      'The effective date cannot be before the notice date.',
      '시행일이 공지일보다 빠를 수 없어요.',
    ]);
  }

  if (form.senderLocal.trim() && !SENDER_LOCAL.test(form.senderLocal.trim())) {
    errors.push([
      'The sender address can only use letters, numbers and . _ % + -',
      '보내는 주소에는 영문·숫자와 . _ % + - 만 쓸 수 있어요.',
    ]);
  }

  const sections = [
    ['Reason for update', '개정 사유', form.reasons, true],
    ['Terms of Use changes', '이용약관 변경 사항', form.termsChanges, form.terms],
    [
      'Privacy Policy changes',
      '개인정보처리방침 변경 사항',
      form.privacyChanges,
      form.privacy,
    ],
  ];
  sections.forEach(([en, ko, rows, enabled]) => {
    if (!enabled) return;
    const items = filled(rows);
    if (items.length === 0) {
      errors.push([`${en}: add at least one line.`, `${ko}: 한 줄 이상 입력해 주세요.`]);
    } else if (items.some((row) => !row.ko || !row.en)) {
      errors.push([
        `${en}: fill in both Korean and English.`,
        `${ko}: 한국어와 영어를 모두 입력해 주세요.`,
      ]);
    }
  });

  return errors;
};
